// Saved views hook — per-user localStorage opslag voor filter-state.
// Fase 6 polish: gebruikers kunnen veel-gebruikte filtersets opslaan en
// herstellen per lijst-scherm. Geen backend nodig (per device, per user via key).

import { useCallback, useEffect, useState } from "react";

interface SavedView<T> { name: string; filter: T; created_at: string }
interface StoredViews<T> { last_filter: T; views: SavedView<T>[] }

function load<T>(key: string, init: T): StoredViews<T> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { last_filter: init, views: [] };
    const parsed = JSON.parse(raw) as Partial<StoredViews<T>>;
    return {
      last_filter: (parsed.last_filter ?? init) as T,
      views: Array.isArray(parsed.views) ? parsed.views : [],
    };
  } catch {
    return { last_filter: init, views: [] };
  }
}

function persist<T>(key: string, data: StoredViews<T>) {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* ignore quota */ }
}

export function useSavedViews<T>(key: string, initial: T) {
  const [{ filter, views }, setState] = useState(() => {
    const loaded = load(key, initial);
    return { filter: loaded.last_filter, views: loaded.views };
  });

  useEffect(() => { persist(key, { last_filter: filter, views }); }, [key, filter, views]);

  const updateFilter = useCallback((patch: Partial<T> | ((prev: T) => T)) => {
    setState((s) => ({
      ...s,
      filter: typeof patch === "function" ? (patch as (p: T) => T)(s.filter) : { ...s.filter, ...patch },
    }));
  }, []);

  const replaceFilter = useCallback((next: T) => setState((s) => ({ ...s, filter: next })), []);

  const saveView = useCallback((name: string) => {
    setState((s) => ({
      ...s,
      views: [
        ...s.views.filter((v) => v.name !== name),
        { name, filter: s.filter, created_at: new Date().toISOString() },
      ].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, []);

  const applyView = useCallback((name: string) => {
    setState((s) => {
      const v = s.views.find((x) => x.name === name);
      return v ? { ...s, filter: v.filter } : s;
    });
  }, []);

  const deleteView = useCallback((name: string) => {
    setState((s) => ({ ...s, views: s.views.filter((v) => v.name !== name) }));
  }, []);

  return { filter, setFilter: updateFilter, replaceFilter, views, saveView, applyView, deleteView };
}
